import { useRateFieldError } from "../../../../hooks/useRateFieldError";
import { FC, useEffect } from "react";

import { Box, Stack } from "@mui/material";
import { browserDefaultTimezone } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateFieldName } from "../../../../hooks/useRateFieldName";

import {
  SCHEDULE_TOOLTIP_SLOT_PROPS,
  scheduleDateFieldSx,
  scheduleRowItemSx,
  scheduleSelectSx,
} from "./scheduleFieldStyles";
import { useCampaignSalesDates } from "./useCampaignSalesDates";
import { useEligibleReferenceRates } from "./useEligibleReferenceRates";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardDateTimePicker } from "@/dashboard-design-system/DateTimePicker";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem/DashboardMenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select/DashboardSelect";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useStableLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface StartsOnFieldsProps {
  isSweepstakes?: boolean;
}

export const StartsOnFields: FC<StartsOnFieldsProps> = ({ isSweepstakes = false }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { control, setValue, getValues, unregister } = useFormContext<StepTicketsFormData>();
  const { apiFormObject: apiTicketing } = useStableLiveFormEditorContext<"ticketing">();

  const startModeField = useRateFieldName("salesStartMode");
  const startDateField = useRateFieldName("salesStartDate");
  const startAfterRateField = useRateFieldName("salesStartAfterRateId");
  const endDateField = useRateFieldName("salesEndDate");

  const startMode = useWatch({ control, name: startModeField });
  const eligibleReferenceRates = useEligibleReferenceRates();
  const timezone = apiTicketing.eventTimezone || browserDefaultTimezone;
  const { hasCustomSalesOpenDate } = useCampaignSalesDates();

  const startDateError = useRateFieldError("salesStartDate");
  const startAfterRateError = useRateFieldError("salesStartAfterRateId");

  useEffect(() => {
    return () => {
      unregister([startDateField, startAfterRateField], { keepValue: true });
    };
  }, [startDateField, startAfterRateField, unregister]);

  const handleModeChange = (mode: string) => {
    setValue(startDateField, null, { shouldDirty: true });
    setValue(startAfterRateField, mode === "afterRate" ? (eligibleReferenceRates[0]?.id ?? null) : null, {
      shouldDirty: true,
    });
  };

  const validateStartBeforeEnd = (value: unknown) => {
    const endDate = getValues(endDateField);
    if (!value || !endDate) {
      return true;
    }
    return new Date(value as Date) < new Date(endDate) || t("dashboard", "ticketing.schedule.startBeforeEndError");
  };

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={0.5} alignItems={{ md: "flex-start" }}>
      <Box sx={scheduleRowItemSx}>
        <DashboardTypography variant="caption" vibe="text-neutral-quiet" sx={{ whiteSpace: "nowrap" }}>
          {t("dashboard", "ticketing.schedule.startsOn")}
        </DashboardTypography>
      </Box>
      <Box sx={{ width: 216, maxWidth: "100%", flexShrink: 0, minHeight: 30 }}>
        <DashboardSelect
          control={control}
          name={startModeField}
          onChange={(event) => handleModeChange(event.target.value as string)}
          size="small"
          sx={scheduleSelectSx}
          data-test="rate-schedule-starts-on-select"
        >
          <DashboardMenuItem value="formDefault">
            {t(
              "dashboard",
              hasCustomSalesOpenDate ? "ticketing.schedule.salesOpeningDate" : "ticketing.schedule.campaignCreation"
            )}
          </DashboardMenuItem>
          <DashboardMenuItem value="customDate">{t("dashboard", "ticketing.schedule.customDate")}</DashboardMenuItem>
          <DashboardMenuItem value="afterRate" disabled={eligibleReferenceRates.length === 0}>
            {t(
              "dashboard",
              isSweepstakes ? "sweepstakes.rates.scheduleAfterRateEnd" : "ticketing.schedule.afterRateEnd"
            )}
          </DashboardMenuItem>
        </DashboardSelect>
      </Box>
      {hasCustomSalesOpenDate && startMode === "formDefault" && (
        <DashboardInformationTooltip
          sx={scheduleRowItemSx}
          title={
            t(
              "dashboard",
              isSweepstakes ? "sweepstakes.rates.scheduleOpeningTooltip" : "ticketing.schedule.salesOpeningTooltip"
            ) as string
          }
          placement="top"
          slotProps={SCHEDULE_TOOLTIP_SLOT_PROPS}
        />
      )}
      {startMode === "customDate" && (
        <Box sx={scheduleDateFieldSx}>
          <DashboardDateTimePicker
            name={startDateField}
            control={control}
            locale={locale}
            rules={[
              isRequired(t("dashboard", "ticketing.schedule.dateRequired")),
              { validate: validateStartBeforeEnd },
              { deps: [endDateField] },
            ]}
            error={Boolean(startDateError?.message)}
            errorMessage={startDateError?.message}
            timezone={timezone}
            size="small"
          />
        </Box>
      )}
      {startMode === "afterRate" && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DashboardSelect
            control={control}
            name={startAfterRateField}
            rules={[
              {
                validate: (value: unknown) => {
                  if (!eligibleReferenceRates.some((eligibleRate) => eligibleRate.id === value)) {
                    return t(
                      "dashboard",
                      isSweepstakes ? "sweepstakes.rates.noEntryRateSelected" : "ticketing.schedule.noTicketSelected"
                    );
                  }
                  const ownEndDate = getValues(endDateField);
                  const referencedEndDate = eligibleReferenceRates.find(
                    (eligibleRate) => eligibleRate.id === value
                  )?.endDate;
                  if (!ownEndDate || !referencedEndDate || referencedEndDate < new Date(ownEndDate)) {
                    return true;
                  }
                  return t("dashboard", "ticketing.schedule.startBeforeEndError");
                },
              },
            ]}
            error={Boolean(startAfterRateError?.message)}
            errorMessage={startAfterRateError?.message}
            size="small"
            sx={scheduleSelectSx}
            data-test="rate-schedule-after-rate-select"
          >
            {eligibleReferenceRates.map((eligibleRate) => (
              <DashboardMenuItem key={eligibleRate.id} value={eligibleRate.id}>
                {eligibleRate.name}
              </DashboardMenuItem>
            ))}
          </DashboardSelect>
        </Box>
      )}
    </Stack>
  );
};
